import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API, formatApiErrorDetail } from '../context/AuthContext';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Eye,
  Trash,
  DotsThree,
  Users,
  Megaphone,
  Buildings
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export function DepartmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const initialFormState = {
    name: '',
    slug: '',
    description: '',
    organization_name: '',
    contact_email: '',
    sender_email: '',
    reply_to_email: '',
    subdomain: '',
    privacy_url: '',
    cookie_url: '',
    kvkk_url: '',
    country: '',
    data_protection_officer: '',
    requires_approval: false,
  };
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  const fetchDepartments = async () => {
    try {
      const { data } = await axios.get(`${API}/departments`, { withCredentials: true });
      setDepartments(data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/departments`, formData, { withCredentials: true });
      toast.success('Abteilung erfolgreich erstellt');
      setShowCreateModal(false);
      setFormData(initialFormState);
      fetchDepartments();
    } catch (error) {
      console.error('Failed to create department:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Abteilung konnte nicht erstellt werden');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/departments/${editingDept.id}`, formData, { withCredentials: true });
      toast.success('Abteilung erfolgreich aktualisiert');
      setEditingDept(null);
      setFormData(initialFormState);
      fetchDepartments();
    } catch (error) {
      console.error('Failed to update department:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Abteilung konnte nicht aktualisiert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Soll diese Abteilung wirklich gelöscht werden?')) return;
    try {
      await axios.delete(`${API}/departments/${id}`, { withCredentials: true });
      toast.success('Abteilung erfolgreich gelöscht');
      fetchDepartments();
    } catch (error) {
      console.error('Failed to delete department:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Abteilung konnte nicht gelöscht werden');
    }
  };

  const openEditModal = (dept) => {
    setFormData({
      name: dept.name,
      slug: dept.slug,
      description: dept.description || '',
      organization_name: dept.organization_name || '',
      contact_email: dept.contact_email || '',
      sender_email: dept.sender_email || '',
      reply_to_email: dept.reply_to_email || '',
      subdomain: dept.subdomain || '',
      privacy_url: dept.privacy_url || '',
      cookie_url: dept.cookie_url || '',
      kvkk_url: dept.kvkk_url || '',
      country: dept.country || '',
      data_protection_officer: dept.data_protection_officer || '',
      requires_approval: dept.requires_approval
    });
    setEditingDept(dept);
  };

  const filteredDepartments = departments.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.slug.toLowerCase().includes(search.toLowerCase())
  );

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-900">Abteilungen</h1>
            <p className="text-zinc-500 mt-1">Mandanten, Datenschutz-Defaults und organisatorische Einheiten verwalten</p>
          </div>
          {['super_admin', 'org_admin'].includes(user?.role) && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              data-testid="create-department-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Abteilung erstellen
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              placeholder="Abteilungen durchsuchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-swiss"
              data-testid="department-search"
            />
          </div>
        </div>

        {/* Departments Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-zinc-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : filteredDepartments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDepartments.map((dept) => (
              <div
                key={dept.id}
                className="card-dashboard p-6"
                data-testid={`department-card-${dept.slug}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#002FA7]/10 rounded-sm flex items-center justify-center">
                    <Buildings className="w-6 h-6 text-[#002FA7]" weight="duotone" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 hover:bg-zinc-100 rounded-sm" data-testid={`dept-menu-${dept.slug}`}>
                        <DotsThree className="w-5 h-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModal(dept)}>
                        <PencilSimple className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </DropdownMenuItem>
                      {['super_admin'].includes(user?.role) && (
                        <DropdownMenuItem onClick={() => handleDelete(dept.id)} className="text-red-600">
                          <Trash className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <h3 className="text-lg font-bold text-zinc-900 mb-1">{dept.name}</h3>
                <p className="text-sm text-zinc-500 mb-4 line-clamp-2">{dept.description || 'Keine Beschreibung'}</p>

                <div className="flex items-center gap-4 text-sm text-zinc-600">
                  <div className="flex items-center gap-1">
                    <Megaphone className="w-4 h-4" />
                    <span>{dept.campaign_count} Kampagnen</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{dept.user_count} Benutzer</span>
                  </div>
                </div>

                {dept.requires_approval && (
                  <div className="mt-3 inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 rounded-sm text-xs font-medium">
                    Freigabe erforderlich
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm">
            <Buildings className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">Keine Abteilungen gefunden</p>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal || !!editingDept} onOpenChange={() => {
          setShowCreateModal(false);
          setEditingDept(null);
          setFormData(initialFormState);
        }}>
          <DialogContent className="modal-brutalist sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editingDept ? 'Abteilung bearbeiten' : 'Abteilung erstellen'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={editingDept ? handleUpdate : handleCreate}>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="label-uppercase">Abteilungsname</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: editingDept ? formData.slug : generateSlug(e.target.value)
                      });
                    }}
                    className="input-swiss"
                    placeholder="z. B. Jugendabteilung"
                    required
                    data-testid="dept-name-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="input-swiss"
                    placeholder="z. B. jugendabteilung"
                    required
                    disabled={!!editingDept}
                    data-testid="dept-slug-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Beschreibung</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-swiss"
                    placeholder="Kurze Beschreibung..."
                    data-testid="dept-description-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Organisationsname</Label>
                  <Input value={formData.organization_name} onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })} className="input-swiss" placeholder="z. B. UNIT Reisen" data-testid="dept-organization-name-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="label-uppercase">Kontakt-E-Mail</Label>
                    <Input value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="input-swiss" placeholder="kontakt@domain.de" data-testid="dept-contact-email-input" />
                  </div>
                  <div>
                    <Label className="label-uppercase">Absender-E-Mail</Label>
                    <Input value={formData.sender_email} onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })} className="input-swiss" placeholder="noreply@domain.de" data-testid="dept-sender-email-input" />
                  </div>
                  <div>
                    <Label className="label-uppercase">Reply-To</Label>
                    <Input value={formData.reply_to_email} onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })} className="input-swiss" placeholder="reply@domain.de" data-testid="dept-replyto-email-input" />
                  </div>
                  <div>
                    <Label className="label-uppercase">Subdomain</Label>
                    <Input value={formData.subdomain} onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })} className="input-swiss" placeholder="hatira-unit" data-testid="dept-subdomain-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="label-uppercase">Privacy URL</Label>
                    <Input value={formData.privacy_url} onChange={(e) => setFormData({ ...formData, privacy_url: e.target.value })} className="input-swiss" placeholder="https://.../datenschutz" data-testid="dept-privacy-url-input" />
                  </div>
                  <div>
                    <Label className="label-uppercase">Cookie URL</Label>
                    <Input value={formData.cookie_url} onChange={(e) => setFormData({ ...formData, cookie_url: e.target.value })} className="input-swiss" placeholder="https://.../cookies" data-testid="dept-cookie-url-input" />
                  </div>
                  <div>
                    <Label className="label-uppercase">KVKK URL</Label>
                    <Input value={formData.kvkk_url} onChange={(e) => setFormData({ ...formData, kvkk_url: e.target.value })} className="input-swiss" placeholder="https://.../kvkk" data-testid="dept-kvkk-url-input" />
                  </div>
                  <div>
                    <Label className="label-uppercase">Land / Rechtsraum</Label>
                    <Input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="input-swiss" placeholder="DE / TR / EU" data-testid="dept-country-input" />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase">Datenschutzbeauftragter</Label>
                  <Input value={formData.data_protection_officer} onChange={(e) => setFormData({ ...formData, data_protection_officer: e.target.value })} className="input-swiss" placeholder="Optional" data-testid="dept-dpo-input" />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requires_approval"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData({ ...formData, requires_approval: e.target.checked })}
                    className="rounded border-zinc-300"
                    data-testid="dept-requires-approval"
                  />
                  <Label htmlFor="requires_approval" className="text-sm font-normal cursor-pointer">
                    Freigabe für das Veröffentlichen von Kampagnen erforderlich
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingDept(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="dept-save-btn">
                  {saving ? 'Speichert...' : (editingDept ? 'Aktualisieren' : 'Erstellen')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
